/**
 * ExemptionModal — Vor-Ort-Ausnahme beantragen (#248).
 *
 * Das Freitextfeld startet immer leer, auch wenn es einen Verlauf gibt: Eine
 * abgelaufene Ausnahme muss neu begründet werden, nicht per Klick verlängert.
 * Die bisherige Begründung steht nur als graues Zitat darunter. Mit `current`
 * (gültige Ausnahme im letzten Monat) wird der Antrag zur Verlängerung: eigener
 * Titel, und die bisherige Begründung ist die der geltenden Ausnahme.
 */
import { Alert, Blockquote, Button, Group, Modal, Select, Stack, Text, Textarea } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EXEMPTION_REASONS, type ExemptionReason, type VisitExemption } from "@msc/shared";
import { useExemptionHistory, useRequestExemption } from "../../hooks/useVisitExemptions.js";

const MIN_NOTE_LENGTH = 15;

interface ExemptionModalProps {
  opened: boolean;
  onClose: () => void;
  customerId: string;
  customerName: string;
  segmentId: string;
  segmentName: string;
  /** Gültige Ausnahme, die verlängert werden soll. */
  current?: VisitExemption | null;
}

export function ExemptionModal({
  opened,
  onClose,
  customerId,
  customerName,
  segmentId,
  segmentName,
  current = null,
}: ExemptionModalProps) {
  const { t } = useTranslation();
  const request = useRequestExemption();
  const history = useExemptionHistory(customerId, segmentId, opened);

  const [reason, setReason] = useState<ExemptionReason>("customer_declines");
  const [note, setNote] = useState("");
  const [showError, setShowError] = useState(false);

  const previous = current ?? history.data?.[0] ?? null;
  // Nur die jüngste Entscheidung zählt: Eine Ablehnung, auf die schon eine
  // Freigabe folgte, ist erledigt und gehört nicht mehr in den Dialog.
  const neuester = history.data?.[0] ?? null;
  const lastRejection = neuester?.status === "rejected" && neuester.decisionNote ? neuester : null;
  const tooShort = note.trim().length < MIN_NOTE_LENGTH;

  function handleClose() {
    setNote("");
    setShowError(false);
    onClose();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (tooShort) {
      setShowError(true);
      return;
    }
    try {
      await request.mutateAsync({ customerId, segmentId, reason, note: note.trim() });
      handleClose();
    } catch {
      // Fehler steht in request.error
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t(current ? "pages.visits.exemption.renewTitle" : "pages.visits.exemption.modalTitle")}
      size="md"
      closeOnClickOutside={false}
    >
      <form onSubmit={(e) => void handleSubmit(e)} noValidate>
        <Stack gap="md">
          <Text size="sm" fw={500}>
            {customerName} · {segmentName}
          </Text>
          <Text size="sm" c="dimmed">
            {t("pages.visits.exemption.modalIntro")}
          </Text>

          <Select
            label={t("pages.visits.exemption.reason")}
            data={EXEMPTION_REASONS.map((r) => ({
              value: r,
              label: t(`pages.visits.exemption.reasons.${r}`),
            }))}
            value={reason}
            onChange={(v) => v && setReason(v as ExemptionReason)}
            allowDeselect={false}
          />

          <Textarea
            label={t("pages.visits.exemption.note")}
            placeholder={t("pages.visits.exemption.notePlaceholder")}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            error={showError && tooShort ? t("pages.visits.exemption.noteTooShort") : undefined}
            minRows={3}
            autosize
          />

          {previous && (
            <Stack gap={4}>
              <Text size="xs" c="dimmed" fw={500}>
                {t("pages.visits.exemption.previous")}
              </Text>
              <Blockquote color="gray" p="xs" mt={0}>
                <Text size="sm" c="dimmed">
                  {t(`pages.visits.exemption.reasons.${previous.reason}`)}
                </Text>
                <Text size="sm" c="dimmed">
                  {previous.note}
                </Text>
              </Blockquote>
            </Stack>
          )}
          {lastRejection?.decisionNote && (
            <Text size="sm" c="dimmed">
              {t("pages.visits.exemption.rejectedHint", { note: lastRejection.decisionNote })}
            </Text>
          )}

          {request.isError && (
            <Alert color="red">
              {request.error instanceof Error ? request.error.message : String(request.error)}
            </Alert>
          )}

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={handleClose} disabled={request.isPending}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={request.isPending}>
              {t("pages.visits.exemption.submit")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
